/**
 * detail.ts — pure derivations for the per-hospital detail hero.
 *
 * These are a FALLBACK. The detail page's authoritative "people waiting now" is
 * the live `/hospitals` overview reading it polls (so the hero total equals the
 * per-triage breakdown's Σ); these derivations fill the hero only until that
 * reading arrives — or when it has none — reconstructing the count from the
 * responses the page still has:
 *   - PRIMARY: the estimated-wait response. `peopleAhead` is CUMULATIVE across
 *     higher-priority triages (a Blu newcomer has every higher-urgency code
 *     ahead of them), so the LARGEST peopleAhead is the whole queue at the
 *     reading instant — a raw integer count, taken at the same `measuredAt`
 *     the freshness badge uses, so it lines up with the overview's totalWaiting.
 *     (Summing peopleAhead would double-count — it is the MAX, never the sum.)
 *   - FALLBACK: the history series' latest non-null bucket per triage, summed
 *     (an averaged, gap-filled, rounded approximation). `history` is OPTIONAL —
 *     the detail screen only passes it in the ≤48h history zoom; the long-window
 *     trend zoom passes `undefined`, leaning on the wait response and live total.
 *
 * All pure and clock-free; the hero injects `now` for the freshness label.
 */

import type { EstimatedWaitResponse, HospitalHistoryResponse } from "@/api/generated/model";
import type { CongestionLevel } from "@/lib/congestion";

/**
 * Current people-waiting total for this hospital, or `null` when neither
 * response has usable data yet (loading / no reading). Always a whole number.
 */
export function currentWaiting(
  wait: EstimatedWaitResponse | undefined,
  history: HospitalHistoryResponse | undefined,
): number | null {
  // Primary: the largest peopleAhead = the whole queue (cumulative ⇒ MAX, not sum).
  if (wait && wait.estimates.length > 0) {
    return Math.round(wait.estimates.reduce((max, e) => Math.max(max, e.peopleAhead), 0));
  }
  // Fallback: sum the latest per-triage history buckets (averaged ⇒ round once).
  if (history && history.series.length > 0) {
    let total = 0;
    let sawValue = false;
    for (const s of history.series) {
      for (let i = s.points.length - 1; i >= 0; i--) {
        const q = s.points[i]?.queueLength;
        if (q !== null && q !== undefined) {
          total += q;
          sawValue = true;
          break;
        }
      }
    }
    if (sawValue) return Math.round(total);
  }
  return null;
}

/**
 * The reading instant to show in the freshness badge: the wait response's real
 * `measuredAt` when present, else whichever `generatedAt` has arrived.
 */
export function detailMeasuredAt(
  wait: EstimatedWaitResponse | undefined,
  history: HospitalHistoryResponse | undefined,
): string | null {
  return wait?.measuredAt ?? wait?.generatedAt ?? history?.generatedAt ?? null;
}

/**
 * One-line, honest lede for the hero, mirroring the overview's tone but scoped
 * to a single ER. `nameIt` is the Italian city name ("Bolzano").
 */
export function detailLede(level: CongestionLevel, nameIt: string): string {
  switch (level) {
    case "busy":
      return `Adesso il pronto soccorso di ${nameIt} è piuttosto affollato.`;
    case "moderate":
      return `Adesso il pronto soccorso di ${nameIt} è movimentato.`;
    case "calm":
      return `Adesso il pronto soccorso di ${nameIt} respira tranquillo.`;
    default:
      return "In attesa della prossima rilevazione in tempo reale.";
  }
}
